import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ArrowRight, ArrowLeft, Check, X, Info, Target, User, Heart, 
  Utensils, Dumbbell, Moon, Brain, Calendar, Pill, Apple, 
  Scale, Ruler, Clock, MapPin, Users, ChevronRight, Sparkles,
  AlertCircle, HelpCircle
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card } from '../../components/ui/card';
import { Checkbox } from '../../components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '../../components/ui/radio-group';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../components/ui/tooltip';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ============== OPTION DEFINITIONS ==============

const GOAL_OPTIONS = [
  { value: 'lose_weight', label: 'Lose weight', icon: Scale },
  { value: 'build_muscle', label: 'Build muscle', icon: Dumbbell },
  { value: 'tone', label: 'Tone up', icon: Target },
  { value: 'maintain', label: 'Maintain current', icon: Check },
  { value: 'improve_energy', label: 'Improve energy/sleep', icon: Moon },
  { value: 'reduce_stress', label: 'Reduce stress', icon: Brain },
  { value: 'not_sure', label: 'Not sure yet', icon: HelpCircle },
];

const SEX_OPTIONS = [
  { value: 'female', label: 'Female' },
  { value: 'male', label: 'Male' },
  { value: 'intersex', label: 'Intersex' },
  { value: 'prefer_not_to_say', label: 'Prefer not to say' },
];

const ETHNICITY_OPTIONS = [
  { value: 'asian', label: 'Asian' },
  { value: 'black', label: 'Black or African American' },
  { value: 'hispanic', label: 'Hispanic or Latino' },
  { value: 'native', label: 'Native American or Alaska Native' },
  { value: 'pacific', label: 'Native Hawaiian or Pacific Islander' },
  { value: 'white', label: 'White' },
  { value: 'other', label: 'Other' },
  { value: 'prefer_not_to_say', label: 'Prefer not to disclose' },
];

const CONDITION_OPTIONS = [
  { value: 'prediabetes', label: 'Prediabetes' },
  { value: 'type2_diabetes', label: 'Type 2 diabetes' },
  { value: 'high_blood_pressure', label: 'High blood pressure' },
  { value: 'high_cholesterol', label: 'High cholesterol' },
  { value: 'fatty_liver', label: 'Fatty liver' },
  { value: 'pcos', label: 'PCOS' },
  { value: 'sleep_apnea', label: 'Sleep apnea' },
  { value: 'none', label: 'None of the above' },
  { value: 'not_sure', label: 'Not sure' },
];

const INJURY_OPTIONS = [
  { value: 'back', label: 'Back/spine issues' },
  { value: 'knee', label: 'Knee problems' },
  { value: 'shoulder', label: 'Shoulder issues' },
  { value: 'ankle', label: 'Ankle/foot problems' },
  { value: 'wrist', label: 'Wrist/hand issues' },
  { value: 'neck', label: 'Neck problems' },
  { value: 'hip', label: 'Hip issues' },
  { value: 'none', label: 'No limitations' },
  { value: 'other', label: 'Other' },
];

const ALLERGY_OPTIONS = [
  { value: 'dairy', label: 'Dairy' },
  { value: 'gluten', label: 'Gluten' },
  { value: 'nuts', label: 'Tree nuts' },
  { value: 'peanuts', label: 'Peanuts' },
  { value: 'shellfish', label: 'Shellfish' },
  { value: 'eggs', label: 'Eggs' },
  { value: 'soy', label: 'Soy' },
  { value: 'fish', label: 'Fish' },
  { value: 'none', label: 'No allergies' },
];

const DIET_PREFERENCE_OPTIONS = [
  { value: 'vegetarian', label: 'Vegetarian' },
  { value: 'vegan', label: 'Vegan' },
  { value: 'pescatarian', label: 'Pescatarian' },
  { value: 'keto', label: 'Keto/Low-carb' },
  { value: 'paleo', label: 'Paleo' },
  { value: 'halal', label: 'Halal' },
  { value: 'kosher', label: 'Kosher' },
  { value: 'none', label: 'No specific diet' },
];

const TRAINING_FREQUENCY_OPTIONS = [
  { value: '0', label: 'Not currently exercising' },
  { value: '1-2', label: '1-2 days per week' },
  { value: '3-4', label: '3-4 days per week' },
  { value: '5-6', label: '5-6 days per week' },
  { value: '7', label: 'Every day' },
];

const WORKOUT_LOCATION_OPTIONS = [
  { value: 'home', label: 'At home' },
  { value: 'gym', label: 'At a gym' },
  { value: 'outdoor', label: 'Outdoors' },
  { value: 'office', label: 'Office/Work' },
  { value: 'none', label: 'I don\'t work out currently' },
];

const WORKOUT_TIME_OPTIONS = [
  { value: 'early_morning', label: 'Early morning (5-7am)' },
  { value: 'morning', label: 'Morning (7-10am)' },
  { value: 'midday', label: 'Midday (10am-2pm)' },
  { value: 'afternoon', label: 'Afternoon (2-5pm)' },
  { value: 'evening', label: 'Evening (5-8pm)' },
  { value: 'night', label: 'Night (8pm+)' },
  { value: 'varies', label: 'It varies' },
];

const SLEEP_OPTIONS = [
  { value: 'less_5', label: 'Less than 5 hours' },
  { value: '5-6', label: '5-6 hours' },
  { value: '6-7', label: '6-7 hours' },
  { value: '7-8', label: '7-8 hours' },
  { value: '8_plus', label: '8+ hours' },
];

const EATING_PATTERN_OPTIONS = [
  { value: 'regular', label: 'Regular meals at set times' },
  { value: 'irregular', label: 'Irregular/varies daily' },
  { value: 'intermittent', label: 'Intermittent fasting' },
  { value: 'grazing', label: 'Frequent small meals' },
];

const MEAL_FREQUENCY_OPTIONS = [
  { value: '1-2', label: '1-2 meals' },
  { value: '3', label: '3 meals' },
  { value: '4-5', label: '4-5 meals (with snacks)' },
  { value: '6_plus', label: '6+ small meals' },
];

const STRESS_OPTIONS = [
  { value: 'low', label: 'Low - Generally relaxed' },
  { value: 'moderate', label: 'Moderate - Some stress' },
  { value: 'high', label: 'High - Often stressed' },
  { value: 'very_high', label: 'Very high - Constantly stressed' },
];

const ENERGY_OPTIONS = [
  { value: 'high', label: 'High energy most days' },
  { value: 'moderate', label: 'Moderate - some ups and downs' },
  { value: 'low', label: 'Often tired or fatigued' },
  { value: 'variable', label: 'Very variable' },
];

const SCHEDULE_OPTIONS = [
  { value: 'regular', label: 'Regular 9-5 schedule' },
  { value: 'early', label: 'Early shift' },
  { value: 'late', label: 'Late/night shift' },
  { value: 'rotating', label: 'Rotating shifts' },
  { value: 'flexible', label: 'Flexible/remote' },
  { value: 'varies', label: 'Varies weekly' },
];

const ACTIVITY_LEVEL_OPTIONS = [
  { value: 'sedentary', label: 'Sedentary - mostly sitting' },
  { value: 'light', label: 'Lightly active - some walking' },
  { value: 'moderate', label: 'Moderately active' },
  { value: 'active', label: 'Very active' },
  { value: 'athletic', label: 'Athletic/intense' },
];

const NUTRITION_CHALLENGE_OPTIONS = [
  { value: 'cravings', label: 'Managing cravings' },
  { value: 'portions', label: 'Portion control' },
  { value: 'meal_prep', label: 'Finding time to cook' },
  { value: 'late_eating', label: 'Late-night eating' },
  { value: 'emotional', label: 'Emotional eating' },
  { value: 'consistency', label: 'Staying consistent' },
  { value: 'budget', label: 'Eating healthy on budget' },
  { value: 'none', label: 'No major challenges' },
];

const RESOURCE_AREAS = [
  { value: 'food', label: 'Food assistance' },
  { value: 'transportation', label: 'Transportation help' },
  { value: 'mental_health', label: 'Mental health support' },
  { value: 'housing', label: 'Housing assistance' },
  { value: 'financial', label: 'Financial assistance' },
  { value: 'healthcare', label: 'Healthcare access' },
];

// ============== SCREEN DEFINITIONS ==============

// Phase-based progress: Phase 1 = Core Health Profile, Phase 2 = Personalization (Optional)
const PHASE1_SCREENS = [
  { id: 'goal', title: 'What are your goals?', subtitle: 'Select all that apply to you.' },
  { id: 'basics', title: 'Let\'s get the basics', subtitle: 'Just a few quick details about you.' },
  { id: 'measurements', title: 'Your measurements', subtitle: 'This helps calculate your baseline.' },
  { id: 'conditions', title: 'Health background', subtitle: 'Do you currently have any of the following?' },
  { id: 'limitations', title: 'Movement considerations', subtitle: 'Any injuries or limitations we should know about?' },
  { id: 'diet', title: 'Dietary preferences', subtitle: 'Help us recommend the right foods for you.' },
  { id: 'lifestyle', title: 'Your lifestyle', subtitle: 'Tell us about your typical routine.' },
  { id: 'nutrition', title: 'Eating habits', subtitle: 'Understanding your current patterns.' },
  { id: 'wellness', title: 'How are you feeling?', subtitle: 'This helps us set the right tone.' },
];

const PHASE2_SCREENS = [
  { id: 'precision_intro', title: 'Improve Personalization', subtitle: 'Share a few details for more precise recommendations.' },
  { id: 'body_composition', title: 'Body composition', subtitle: 'Optional measurements for precision.' },
  { id: 'labs', title: 'Recent labs', subtitle: 'Have you had bloodwork done recently?' },
  { id: 'energy_schedule', title: 'Energy & schedule', subtitle: 'Understanding your daily rhythm.' },
  { id: 'nutrition_challenges', title: 'Nutrition challenges', subtitle: 'What\'s been hardest for you?' },
  { id: 'care_support', title: 'Care & support', subtitle: 'Connecting you with resources.' },
];

// ============== HELPER COMPONENTS ==============

const PhaseProgress = ({ currentScreen, totalScreens, phase, isOptional }) => (
  <div className="mb-6">
    {/* Phase indicator */}
    <div className="flex items-center justify-between mb-3">
      <div className="flex items-center gap-2">
        <span className={`px-3 py-1 rounded-full text-xs font-medium ${
          phase === 1 
            ? 'bg-primary/10 text-primary' 
            : 'bg-[#8BB89F]/10 text-[#8BB89F]'
        }`}>
          Phase {phase}: {phase === 1 ? 'Core Profile' : 'Personalization'}
        </span>
        {isOptional && (
          <span className="text-xs text-muted-foreground italic">(Optional)</span>
        )}
      </div>
    </div>
    
    {/* Progress bar */}
    <div className="h-1.5 bg-muted rounded-full overflow-hidden">
      <motion.div 
        className={`h-full rounded-full ${phase === 1 ? 'bg-primary' : 'bg-[#8BB89F]'}`}
        initial={{ width: 0 }}
        animate={{ width: `${((currentScreen + 1) / totalScreens) * 100}%` }}
        transition={{ duration: 0.3 }}
      />
    </div>
    
    {/* Encouragement message near end of Phase 1 */}
    {currentScreen >= totalScreens - 3 && phase === 1 && (
      <motion.p 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="text-xs text-primary mt-2 text-center"
      >
        Almost there! You&apos;re doing great.
      </motion.p>
    )}
  </div>
);

// Multi-select for goals (supports multiple selections)
const GoalMultiSelect = ({ options, values, onChange }) => {
  const handleToggle = (value) => {
    if (value === 'not_sure') {
      // "Not sure yet" is exclusive - selecting it clears others
      if (values.includes(value)) {
        onChange([]);
      } else {
        onChange([value]);
      }
    } else {
      // Selecting any other option removes "not_sure" if present and allows multi-select
      let newValues = values.filter(v => v !== 'not_sure');
      if (newValues.includes(value)) {
        newValues = newValues.filter(v => v !== value);
      } else {
        newValues = [...newValues, value];
      }
      onChange(newValues);
    }
  };

  const isNotSureSelected = values.includes('not_sure');

  return (
    <div className="grid grid-cols-1 gap-2">
      {options.map((option) => {
        const Icon = option.icon;
        const isSelected = values.includes(option.value);
        return (
          <motion.button
            key={option.value}
            type="button"
            whileTap={{ scale: 0.98 }}
            onClick={() => handleToggle(option.value)}
            className={`flex items-center gap-3 p-4 rounded-xl border-2 transition-all text-left ${
              isSelected 
                ? 'border-primary bg-primary/10 shadow-sm' 
                : isNotSureSelected && option.value !== 'not_sure'
                  ? 'border-border opacity-60 hover:opacity-100 bg-card'
                  : 'border-border hover:border-primary/30 bg-card'
            }`}
            data-testid={`goal-${option.value}`}
          >
            {Icon && <Icon size={18} className={isSelected ? 'text-primary' : 'text-muted-foreground'} />}
            <span className={`text-sm font-medium flex-1 ${isSelected ? 'text-primary' : 'text-foreground'}`}>
              {option.label}
            </span>
            <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
              isSelected ? 'bg-primary border-primary' : 'border-muted-foreground'
            }`}>
              {isSelected && <Check size={12} className="text-white" />}
            </div>
          </motion.button>
        );
      })}
    </div>
  );
};

const SingleSelect = ({ options, value, onChange, columns = 1, hasError = false }) => (
  <div className={`grid gap-2 ${columns === 2 ? 'grid-cols-2' : 'grid-cols-1'}`}>
    {options.map((option) => {
      const Icon = option.icon;
      const isSelected = value === option.value;
      return (
        <motion.button
          key={option.value}
          type="button"
          whileTap={{ scale: 0.98 }}
          onClick={() => onChange(option.value)}
          className={`flex items-center gap-3 p-4 rounded-xl border-2 transition-all text-left ${
            isSelected 
              ? 'border-primary bg-primary/10 shadow-sm' 
              : hasError
                ? 'border-[#D54B48]/50 hover:border-[#D54B48] bg-card'
                : 'border-border hover:border-primary/30 bg-card'
          }`}
          data-testid={`option-${option.value}`}
        >
          {Icon && <Icon size={18} className={isSelected ? 'text-primary' : 'text-muted-foreground'} />}
          <span className={`text-sm font-medium ${isSelected ? 'text-primary' : 'text-foreground'}`}>
            {option.label}
          </span>
          {isSelected && <Check size={16} className="ml-auto text-primary" />}
        </motion.button>
      );
    })}
  </div>
);

const MultiSelect = ({ options, values, onChange, maxSelections, noneOption = 'none', hasError = false }) => {
  const handleToggle = (value) => {
    // Check if this option is marked as exclusive (none, not_sure, etc.)
    const isExclusive = value === noneOption || value === 'not_sure';
    
    if (isExclusive) {
      // If clicking an exclusive option when already selected, deselect it (reversible)
      if (values.includes(value)) {
        onChange([]);
      } else {
        // Selecting exclusive option clears all others
        onChange([value]);
      }
    } else {
      // Selecting a regular option removes any exclusive options and allows multi-select
      let newValues = values.filter(v => v !== noneOption && v !== 'not_sure');
      if (newValues.includes(value)) {
        newValues = newValues.filter(v => v !== value);
      } else {
        if (maxSelections && newValues.length >= maxSelections) {
          toast.info(`You can select up to ${maxSelections} options`);
          return;
        }
        newValues = [...newValues, value];
      }
      onChange(newValues);
    }
  };

  const isExclusiveSelected = values.includes(noneOption) || values.includes('not_sure');

  return (
    <div className="grid grid-cols-1 gap-2">
      {options.map((option) => {
        const isSelected = values.includes(option.value);
        const isExclusive = option.value === noneOption || option.value === 'not_sure';
        return (
          <motion.button
            key={option.value}
            type="button"
            whileTap={{ scale: 0.98 }}
            onClick={() => handleToggle(option.value)}
            className={`flex items-center gap-3 p-3 rounded-xl border transition-all text-left ${
              isSelected 
                ? 'border-primary bg-primary/10' 
                : isExclusiveSelected && !isExclusive
                  ? 'border-border opacity-60 hover:opacity-100 bg-card'
                  : hasError
                    ? 'border-[#D54B48]/50 hover:border-[#D54B48] bg-card'
                    : 'border-border hover:border-primary/30 bg-card'
            }`}
            data-testid={`multi-${option.value}`}
          >
            <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
              isSelected ? 'bg-primary border-primary' : 'border-muted-foreground'
            }`}>
              {isSelected && <Check size={12} className="text-white" />}
            </div>
            <span className={`text-sm ${isSelected ? 'text-primary font-medium' : 'text-foreground'}`}>
              {option.label}
            </span>
          </motion.button>
        );
      })}
    </div>
  );
};

const NumericInput = ({ label, value, onChange, unit, min, max, placeholder, hasError = false }) => {
  const [error, setError] = useState('');
  
  const handleChange = (e) => {
    const val = e.target.value;
    onChange(val);
    
    if (val && (Number(val) < min || Number(val) > max)) {
      setError(`Please enter a value between ${min} and ${max}`);
    } else {
      setError('');
    }
  };

  return (
    <div className="space-y-1">
      <Label className={`text-sm text-muted-foreground ${hasError ? 'text-[#D54B48]' : ''}`}>
        {label} {hasError && <span className="text-[#D54B48]">*</span>}
      </Label>
      <div className="relative">
        <Input
          type="number"
          value={value}
          onChange={handleChange}
          placeholder={placeholder}
          className={`pr-12 ${error ? 'border-amber-500' : hasError ? 'border-[#D54B48]' : ''}`}
          min={min}
          max={max}
        />
        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
          {unit}
        </span>
      </div>
      {error && <p className="text-xs text-amber-500">{error}</p>}
    </div>
  );
};

// ============== MAIN COMPONENT ==============

export default function OnboardingWizard() {
  const navigate = useNavigate();
  const { user, updateUser } = useAuth();
  const [phase, setPhase] = useState(1); // 1 or 2
  const [screenIndex, setScreenIndex] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showCompletion, setShowCompletion] = useState(false);
  const [attemptedNext, setAttemptedNext] = useState(false); // Track if user tried to continue without completing
  
  // Form state
  const [formData, setFormData] = useState({
    // Phase 1 - Required
    goals: [], // Changed to array for multi-select
    goal_type: '', // Keep for backwards compat
    sex_at_birth: '',
    // Note: age is calculated from DOB during registration, not collected here
    height_feet: '',
    height_inches: '',
    weight: '',
    cultural_tags: [],
    chronic_conditions: [],
    movement_limitations: [],
    other_limitation: '',
    food_allergies: [],
    dietary_preferences: [],
    pregnancy_status: '',
    has_medications: '',
    medications: [],
    supplements: [],
    training_frequency: '',
    workout_locations: [], // Changed to array for multi-select
    workout_time_preference: '',
    sleep_hours_avg: '',
    eating_pattern: '',
    meal_frequency: '',
    stress_level: '',
    resource_support_opt_in: '',
    
    // Phase 2 - Optional
    waist_circumference: '',
    labs_available: '',
    last_lab_date: '',
    lab_types: [],
    energy_baseline: '',
    schedule_type: '',
    activity_level_baseline: '',
    nutrition_challenges: [],
    has_pcp: '',
    resource_areas_requested: [],
    
    // Meta
    precision_status: 'pending', // pending, completed, declined
    skipped_phase2_questions: [], // Track which Phase 2 questions were skipped
  });

  const screens = phase === 1 ? PHASE1_SCREENS : PHASE2_SCREENS;
  const currentScreen = screens[screenIndex];

  const updateForm = useCallback((field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setAttemptedNext(false); // Clear validation when user makes changes
  }, []);

  const canContinue = () => {
    switch (currentScreen?.id) {
      case 'goal':
        return formData.goals.length > 0;
      case 'basics':
        return !!formData.sex_at_birth; // Age is calculated from DOB at registration
      case 'measurements':
        return !!formData.height_feet && !!formData.weight;
      case 'conditions':
        return formData.chronic_conditions.length > 0;
      case 'limitations':
        return formData.movement_limitations.length > 0;
      case 'diet':
        return formData.food_allergies.length > 0 && formData.dietary_preferences.length > 0;
      case 'lifestyle':
        return !!formData.training_frequency && formData.workout_locations.length > 0 && !!formData.sleep_hours_avg;
      case 'nutrition':
        return !!formData.eating_pattern && !!formData.meal_frequency;
      case 'wellness':
        return !!formData.stress_level;
      default:
        return true; // Phase 2 screens are optional
    }
  };

  // Get specific validation error message
  const getValidationError = () => {
    if (!attemptedNext || canContinue()) return null;
    
    switch (currentScreen?.id) {
      case 'goal':
        return 'Please select at least one goal';
      case 'basics':
        if (!formData.sex_at_birth) return 'Please select sex assigned at birth';
        return null;
      case 'measurements':
        if (!formData.height_feet) return 'Please enter your height';
        if (!formData.weight) return 'Please enter your weight';
        return null;
      case 'conditions':
        return 'Please select at least one option (or "None of the above")';
      case 'limitations':
        return 'Please select at least one option (or "No limitations")';
      case 'diet':
        if (formData.food_allergies.length === 0) return 'Please select at least one allergy option';
        if (formData.dietary_preferences.length === 0) return 'Please select at least one dietary preference';
        return null;
      case 'lifestyle':
        if (!formData.training_frequency) return 'Please select your current exercise frequency';
        if (formData.workout_locations.length === 0) return 'Please select at least one workout location';
        if (!formData.sleep_hours_avg) return 'Please select your sleep hours';
        return null;
      case 'nutrition':
        if (!formData.eating_pattern) return 'Please select your eating pattern';
        if (!formData.meal_frequency) return 'Please select your meal frequency';
        return null;
      case 'wellness':
        return 'Please select your stress level';
      default:
        return null;
    }
  };

  const handleNext = async () => {
    if (!canContinue() && phase === 1) {
      setAttemptedNext(true);
      toast.error(getValidationError() || 'Please complete this step to continue');
      return;
    }

    if (screenIndex < screens.length - 1) {
      setScreenIndex(prev => prev + 1);
      setAttemptedNext(false);
    } else if (phase === 1) {
      // End of Phase 1 - show completion and offer Phase 2
      await submitPhase1();
    } else {
      // End of Phase 2
      await submitPhase2();
    }
  };

  const handleBack = () => {
    setAttemptedNext(false);
    if (screenIndex > 0) {
      setScreenIndex(prev => prev - 1);
    } else if (phase === 2) {
      // Go back to Phase 1
      setPhase(1);
      setScreenIndex(PHASE1_SCREENS.length - 1);
    }
  };

  const handleSkipPhase2 = async () => {
    updateForm('precision_status', 'declined');
    await submitOnboarding(true);
  };

  // Skip individual question in Phase 2
  const handleSkipQuestion = () => {
    // Track that this question was skipped
    const skipped = [...(formData.skipped_phase2_questions || []), currentScreen?.id];
    setFormData(prev => ({ ...prev, skipped_phase2_questions: skipped }));
    
    // Move to next screen
    if (screenIndex < screens.length - 1) {
      setScreenIndex(prev => prev + 1);
    } else {
      // End of Phase 2
      submitPhase2();
    }
  };

  const submitPhase1 = async () => {
    // Set goal_type for backwards compatibility (first selected goal)
    if (formData.goals.length > 0) {
      setFormData(prev => ({ ...prev, goal_type: prev.goals[0] }));
    }
    setShowCompletion(true);
    // After showing completion, offer Phase 2
    setTimeout(() => {
      setShowCompletion(false);
      setPhase(2);
      setScreenIndex(0);
    }, 2000);
  };

  const submitPhase2 = async () => {
    updateForm('precision_status', 'completed');
    await submitOnboarding(false);
  };

  const submitOnboarding = async (skippedPhase2) => {
    setIsSubmitting(true);
    try {
      const payload = {
        ...formData,
        goal_type: formData.goals[0] || formData.goal_type,
        health_goals: formData.goals,
        height_inches_total: formData.height_feet ? (parseInt(formData.height_feet) * 12) + (parseInt(formData.height_inches) || 0) : null,
        precision_status: skippedPhase2 ? 'declined' : 'completed',
      };
      
      await axios.post(`${API}/user/onboarding-complete`, payload);
      updateUser({ onboarding_completed: true });
      toast.success('Welcome to HealthLink360!');
      navigate('/dashboard');
    } catch (error) {
      console.error('Onboarding error:', error);
      toast.error('Failed to save. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // ============== SCREEN RENDERS ==============

  const renderScreen = () => {
    const validationError = getValidationError();
    const showValidationError = attemptedNext && validationError;
    
    switch (currentScreen?.id) {
      // PHASE 1 SCREENS
      case 'goal':
        return (
          <div className="space-y-4">
            <GoalMultiSelect
              options={GOAL_OPTIONS}
              values={formData.goals}
              onChange={(v) => updateForm('goals', v)}
            />
            {showValidationError && (
              <motion.p
                initial={{ opacity: 0, y: -5 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-sm text-[#D54B48] flex items-center gap-2"
              >
                <AlertCircle size={14} />
                {validationError}
              </motion.p>
            )}
          </div>
        );

      case 'basics':
        return (
          <div className="space-y-6">
            <div>
              <Label className={`text-sm font-medium mb-3 block ${showValidationError && !formData.sex_at_birth ? 'text-[#D54B48]' : ''}`}>
                Sex assigned at birth {showValidationError && !formData.sex_at_birth && <span className="text-[#D54B48]">*</span>}
              </Label>
              <SingleSelect
                options={SEX_OPTIONS}
                value={formData.sex_at_birth}
                onChange={(v) => updateForm('sex_at_birth', v)}
                columns={2}
                hasError={showValidationError && !formData.sex_at_birth}
              />
            </div>
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Label className="text-sm font-medium">Race/Ethnicity</Label>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button type="button" className="p-0.5">
                        <Info size={14} className="text-muted-foreground" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent className="max-w-[200px]">
                      <p className="text-xs">Used only for culturally relevant content. Not used for medical decisions. Select all that apply.</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
                <span className="text-xs text-muted-foreground ml-auto">Optional</span>
              </div>
              <MultiSelect
                options={ETHNICITY_OPTIONS}
                values={formData.cultural_tags}
                onChange={(v) => updateForm('cultural_tags', v)}
                noneOption="prefer_not_to_say"
              />
            </div>
          </div>
        );

      case 'measurements':
        return (
          <div className="space-y-6">
            <div>
              <Label className="text-sm font-medium mb-3 block">Height</Label>
              <div className="grid grid-cols-2 gap-3">
                <NumericInput
                  label=""
                  value={formData.height_feet}
                  onChange={(v) => updateForm('height_feet', v)}
                  unit="ft"
                  min={3}
                  max={8}
                  placeholder="Feet"
                />
                <NumericInput
                  label=""
                  value={formData.height_inches}
                  onChange={(v) => updateForm('height_inches', v)}
                  unit="in"
                  min={0}
                  max={11}
                  placeholder="Inches"
                />
              </div>
            </div>
            <NumericInput
              label="Weight"
              value={formData.weight}
              onChange={(v) => updateForm('weight', v)}
              unit="lbs"
              min={50}
              max={700}
              placeholder="Enter your weight"
            />
            {formData.sex_at_birth === 'female' && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                className="pt-4 border-t border-border"
              >
                <Label className="text-sm font-medium mb-3 block">Are you currently pregnant or nursing?</Label>
                <SingleSelect
                  options={[
                    { value: 'no', label: 'No' },
                    { value: 'pregnant', label: 'Yes, pregnant' },
                    { value: 'nursing', label: 'Yes, nursing' },
                    { value: 'prefer_not_to_say', label: 'Prefer not to say' },
                  ]}
                  value={formData.pregnancy_status}
                  onChange={(v) => updateForm('pregnancy_status', v)}
                  columns={2}
                />
              </motion.div>
            )}
          </div>
        );

      case 'conditions':
        return (
          <MultiSelect
            options={CONDITION_OPTIONS}
            values={formData.chronic_conditions}
            onChange={(v) => updateForm('chronic_conditions', v)}
            noneOption="none"
          />
        );

      case 'limitations':
        return (
          <div className="space-y-4">
            <MultiSelect
              options={INJURY_OPTIONS}
              values={formData.movement_limitations}
              onChange={(v) => updateForm('movement_limitations', v)}
              noneOption="none"
            />
            {formData.movement_limitations.includes('other') && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
              >
                <Input
                  placeholder="Please describe..."
                  value={formData.other_limitation}
                  onChange={(e) => updateForm('other_limitation', e.target.value)}
                />
              </motion.div>
            )}
          </div>
        );

      case 'diet':
        return (
          <div className="space-y-6">
            <div>
              <Label className="text-sm font-medium mb-3 block">Any food allergies?</Label>
              <MultiSelect
                options={ALLERGY_OPTIONS}
                values={formData.food_allergies}
                onChange={(v) => updateForm('food_allergies', v)}
                noneOption="none"
              />
            </div>
            <div>
              <Label className="text-sm font-medium mb-3 block">Dietary preferences</Label>
              <MultiSelect
                options={DIET_PREFERENCE_OPTIONS}
                values={formData.dietary_preferences}
                onChange={(v) => updateForm('dietary_preferences', v)}
                noneOption="none"
              />
            </div>
          </div>
        );

      case 'lifestyle':
        return (
          <div className="space-y-6">
            <div>
              <Label className="text-sm font-medium mb-2 block">How often do you currently exercise?</Label>
              <p className="text-xs text-muted-foreground mb-3">
                This helps us understand your starting point. Coach360 will recommend an optimal rhythm based on your goals.
              </p>
              <SingleSelect
                options={TRAINING_FREQUENCY_OPTIONS}
                value={formData.training_frequency}
                onChange={(v) => updateForm('training_frequency', v)}
              />
            </div>
            <div>
              <Label className="text-sm font-medium mb-2 block">Where do you usually work out?</Label>
              <p className="text-xs text-muted-foreground mb-3">Select all that apply</p>
              <MultiSelect
                options={WORKOUT_LOCATION_OPTIONS}
                values={formData.workout_locations}
                onChange={(v) => updateForm('workout_locations', v)}
                noneOption="none"
              />
            </div>
            <div>
              <Label className="text-sm font-medium mb-3 block">Average sleep per night?</Label>
              <SingleSelect
                options={SLEEP_OPTIONS}
                value={formData.sleep_hours_avg}
                onChange={(v) => updateForm('sleep_hours_avg', v)}
              />
            </div>
          </div>
        );

      case 'nutrition':
        return (
          <div className="space-y-6">
            <div>
              <Label className="text-sm font-medium mb-3 block">What&apos;s your typical eating pattern?</Label>
              <SingleSelect
                options={EATING_PATTERN_OPTIONS}
                value={formData.eating_pattern}
                onChange={(v) => updateForm('eating_pattern', v)}
              />
            </div>
            <div>
              <Label className="text-sm font-medium mb-3 block">How many meals do you eat per day?</Label>
              <SingleSelect
                options={MEAL_FREQUENCY_OPTIONS}
                value={formData.meal_frequency}
                onChange={(v) => updateForm('meal_frequency', v)}
                columns={2}
              />
            </div>
          </div>
        );

      case 'wellness':
        return (
          <div className="space-y-6">
            <div>
              <Label className="text-sm font-medium mb-3 block">How would you rate your current stress level?</Label>
              <SingleSelect
                options={STRESS_OPTIONS}
                value={formData.stress_level}
                onChange={(v) => updateForm('stress_level', v)}
              />
            </div>
            <div>
              <Label className="text-sm font-medium mb-3 block">Would you like to access community resources?</Label>
              <p className="text-xs text-muted-foreground mb-3">Food assistance, transportation, mental health support, etc.</p>
              <SingleSelect
                options={[
                  { value: 'yes', label: 'Yes, show me resources' },
                  { value: 'no', label: 'No thanks' },
                  { value: 'later', label: 'Maybe later' },
                ]}
                value={formData.resource_support_opt_in}
                onChange={(v) => updateForm('resource_support_opt_in', v)}
              />
            </div>
          </div>
        );

      // PHASE 2 SCREENS
      case 'precision_intro':
        return (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6 }}
            className="text-center space-y-6 py-8"
          >
            <motion.div 
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.2, duration: 0.4 }}
              className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mx-auto"
            >
              <Sparkles size={36} className="text-primary" />
            </motion.div>
            <div className="space-y-3">
              <h3 className="font-heading font-bold text-2xl">Improve Personalization</h3>
              <p className="text-muted-foreground leading-relaxed max-w-sm mx-auto">
                Share a few details for more precise recommendations.
              </p>
            </div>
            <Card className="glass-card p-5 text-left border-l-4 border-l-[#8BB89F]">
              <p className="text-sm text-muted-foreground leading-relaxed">
                This section is <span className="text-[#8BB89F] font-semibold">completely optional</span>.{' '}
                You can skip it now and update later from your profile.
              </p>
            </Card>
          </motion.div>
        );

      case 'body_composition':
        return (
          <div className="space-y-6">
            <NumericInput
              label="Waist circumference (optional)"
              value={formData.waist_circumference}
              onChange={(v) => updateForm('waist_circumference', v)}
              unit="in"
              min={20}
              max={70}
              placeholder="Measure at navel"
            />
            <p className="text-xs text-muted-foreground">
              Measuring at your navel while relaxed gives the most accurate reading.
            </p>
          </div>
        );

      case 'labs':
        return (
          <div className="space-y-6">
            <div>
              <Label className="text-sm font-medium mb-3 block">Have you had bloodwork done recently?</Label>
              <SingleSelect
                options={[
                  { value: 'yes', label: 'Yes, within the last year' },
                  { value: 'no', label: 'No, or not recently' },
                  { value: 'not_sure', label: 'Not sure' },
                ]}
                value={formData.labs_available}
                onChange={(v) => updateForm('labs_available', v)}
              />
            </div>
            {formData.labs_available === 'yes' && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                className="space-y-4"
              >
                <Input
                  type="date"
                  value={formData.last_lab_date}
                  onChange={(e) => updateForm('last_lab_date', e.target.value)}
                  className="w-full"
                />
                <p className="text-xs text-muted-foreground">
                  In a future update, you&apos;ll be able to connect your lab results for deeper insights.
                </p>
              </motion.div>
            )}
          </div>
        );

      case 'energy_schedule':
        return (
          <div className="space-y-6">
            <div>
              <Label className="text-sm font-medium mb-3 block">How&apos;s your energy most days?</Label>
              <SingleSelect
                options={ENERGY_OPTIONS}
                value={formData.energy_baseline}
                onChange={(v) => updateForm('energy_baseline', v)}
              />
            </div>
            <div>
              <Label className="text-sm font-medium mb-3 block">What&apos;s your typical work schedule?</Label>
              <SingleSelect
                options={SCHEDULE_OPTIONS}
                value={formData.schedule_type}
                onChange={(v) => updateForm('schedule_type', v)}
              />
            </div>
            <div>
              <Label className="text-sm font-medium mb-3 block">Overall activity level?</Label>
              <SingleSelect
                options={ACTIVITY_LEVEL_OPTIONS}
                value={formData.activity_level_baseline}
                onChange={(v) => updateForm('activity_level_baseline', v)}
              />
            </div>
          </div>
        );

      case 'nutrition_challenges':
        return (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">Select up to 2 that apply most.</p>
            <MultiSelect
              options={NUTRITION_CHALLENGE_OPTIONS}
              values={formData.nutrition_challenges}
              onChange={(v) => updateForm('nutrition_challenges', v)}
              maxSelections={2}
              noneOption="none"
            />
          </div>
        );

      case 'care_support':
        return (
          <div className="space-y-6">
            <div>
              <Label className="text-sm font-medium mb-3 block">Do you have a primary care provider?</Label>
              <SingleSelect
                options={[
                  { value: 'yes', label: 'Yes' },
                  { value: 'no', label: 'No' },
                  { value: 'looking', label: 'Looking for one' },
                ]}
                value={formData.has_pcp}
                onChange={(v) => updateForm('has_pcp', v)}
              />
            </div>
            {formData.resource_support_opt_in === 'yes' && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                className="space-y-4"
              >
                <Label className="text-sm font-medium block">What areas might you need support with?</Label>
                <MultiSelect
                  options={RESOURCE_AREAS}
                  values={formData.resource_areas_requested}
                  onChange={(v) => updateForm('resource_areas_requested', v)}
                />
              </motion.div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  // Completion animation
  if (showCompletion) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center space-y-6"
        >
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
            className="w-16 h-16 rounded-full border-4 border-primary border-t-transparent mx-auto"
          />
          <h2 className="font-heading font-bold text-xl">Building your personalized plan...</h2>
          <p className="text-muted-foreground">This will just take a moment.</p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="onboarding-wizard">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-background/80 backdrop-blur-lg border-b border-border">
        <div className="flex items-center justify-between px-4 py-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={handleBack}
            disabled={phase === 1 && screenIndex === 0}
            className="rounded-full"
          >
            <ArrowLeft size={20} />
          </Button>
          
          {phase === 2 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleSkipPhase2}
              className="text-[#8BB89F] hover:text-[#8BB89F]/80 hover:bg-[#8BB89F]/10"
              data-testid="skip-phase2-top"
            >
              Skip for now
            </Button>
          )}
        </div>
      </header>

      <main className="px-6 py-6 max-w-md mx-auto">
        {/* Progress */}
        <PhaseProgress
          currentScreen={screenIndex}
          totalScreens={screens.length}
          phase={phase}
          isOptional={phase === 2}
        />

        {/* Title */}
        <motion.div
          key={currentScreen?.id}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -20 }}
          className="mb-6"
        >
          <h1 className="font-heading font-bold text-2xl mb-1">{currentScreen?.title}</h1>
          <p className="text-muted-foreground text-sm">{currentScreen?.subtitle}</p>
        </motion.div>

        {/* Screen Content */}
        <AnimatePresence mode="wait">
          <motion.div
            key={currentScreen?.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.2 }}
          >
            {renderScreen()}
          </motion.div>
        </AnimatePresence>

        {/* Continue Button */}
        <div className="mt-8 space-y-3">
          <Button
            onClick={handleNext}
            disabled={isSubmitting}
            className="w-full rounded-full h-12"
            data-testid="continue-btn"
          >
            {isSubmitting ? (
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                className="w-5 h-5 border-2 border-white border-t-transparent rounded-full"
              />
            ) : screenIndex === screens.length - 1 ? (
              phase === 1 ? 'Continue to Personalization' : 'Finish Setup'
            ) : (
              <>
                Continue
                <ArrowRight size={18} className="ml-2" />
              </>
            )}
          </Button>

          {/* Phase 2: Skip this question (for individual questions, not intro) */}
          {phase === 2 && currentScreen?.id !== 'precision_intro' && (
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={handleSkipQuestion}
                className="flex-1 text-muted-foreground hover:text-foreground border-dashed"
                data-testid="skip-question-btn"
              >
                Skip this question
              </Button>
              <Button
                variant="ghost"
                onClick={handleSkipPhase2}
                className="text-[#8BB89F] hover:text-[#8BB89F]/80 hover:bg-[#8BB89F]/10 whitespace-nowrap"
                data-testid="skip-phase2-bottom"
              >
                Skip all
              </Button>
            </div>
          )}
        </div>

        {/* Validation hint */}
        {attemptedNext && !canContinue() && phase === 1 && (
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center text-xs text-[#D54B48] mt-4 flex items-center justify-center gap-1"
          >
            <AlertCircle size={12} />
            {getValidationError()}
          </motion.p>
        )}
      </main>
    </div>
  );
}
